/**
 * Company administration. Owner-only for creation; a company admin sees and
 * edits their own.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import CompanyEditDialog from '../../components/CompanyEditDialog';
import { CompanyLogo } from '../../components/CompanyLogo';
import CompanyFields, {
  EMPTY_COMPANY,
  companyPayload,
  type CompanyFieldValues,
} from '../../components/CompanyFields';
import { Link } from 'react-router-dom';
import { canSeeAllCompanies, useAuth } from '../../store/auth';
import type { Company, Office, Page, ShiftPolicy } from '../../types';

export default function CompaniesPage() {
  const me = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  /** The company being corrected. Owner and Company Admin only; the API
   *  enforces the same, so the button being absent is courtesy. */
  const [editing, setEditing] = useState<Company | null>(null);

  /**
   * Every company's offices and policies, in two requests rather than two per
   * card. skipActingCompany for the same reason the company list uses it:
   * this screen is about all of them, and narrowing to the acting company
   * would make every other card look unconfigured.
   */
  const { data: offices } = useQuery<Page<Office>>({
    queryKey: ['setup-offices'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/offices', {
          skipActingCompany: true,
          params: { limit: 200 },
        })
      ).data,
  });

  const { data: policies } = useQuery<Page<ShiftPolicy>>({
    queryKey: ['setup-policies'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/shift-policies', {
          skipActingCompany: true,
          params: { limit: 200 },
        })
      ).data,
  });

  const { data, isLoading } = useQuery<Page<Company>>({
    queryKey: ['admin-companies'],
    queryFn: async () =>
      (
        // Listing companies must never be narrowed to one company.
        await client.get('/api/v1/admin/companies', {
          skipActingCompany: true,
          params: { limit: 200, includeInactive: true },
        })
      ).data,
  });

  const updateCompany = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      (await client.patch(`/api/v1/admin/companies/${id}`, body)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      // The card's state warning and the setup checklist both read these.
      queryClient.invalidateQueries({ queryKey: ['company-stats', data.id] });
      setEditing(null);
      toast.success(`${data.name} updated`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not update the company')),
  });

  const createCompany = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/companies', body)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      // The card's checklist reads these, and a company created a second ago
      // has neither — without this the new card renders complete until
      // something else refetches.
      queryClient.invalidateQueries({ queryKey: ['setup-offices'] });
      queryClient.invalidateQueries({ queryKey: ['setup-policies'] });
      setShowForm(false);
      // Not "created" and nothing else. A company with no workplace and no
      // monitoring policy cannot take a hire without dropping them into
      // hardcoded times, and the moment somebody has just made one is the
      // moment to say so.
      toast.success(`${data.name} created — it still needs setting up`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not create the company')),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">
          Companies{' '}
          {data && <span className="badge bg-gray-100 text-gray-600">{data.total}</span>}
        </h2>
        {canSeeAllCompanies(me) && (
          <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'Add company'}
          </button>
        )}
      </div>

      {showForm && (
        <CompanyForm submitting={createCompany.isPending} onSubmit={createCompany.mutate} />
      )}

      {isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {data?.items.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onEdit={() => setEditing(company)}
              offices={
                offices?.items.filter((o) => o.companyId === company.id).length
              }
              policies={
                policies?.items.filter((p) => p.companyId === company.id).length
              }
            />
          ))}
        </div>
      )}
      {editing && (
        <CompanyEditDialog
          key={editing.id}
          company={editing}
          saving={updateCompany.isPending}
          onCancel={() => setEditing(null)}
          onSave={(body) => updateCompany.mutate({ id: editing.id, body })}
        />
      )}

    </div>
  );
}

function CompanyCard({
  onEdit,
  company,
  offices,
  policies,
}: {
  company: Company;
  /** Omitted where the viewer may not edit; the API refuses either way. */
  onEdit?: () => void;
  /** Undefined while loading — distinct from zero, which is the problem. */
  offices: number | undefined;
  policies: number | undefined;
}) {
  const { data: stats } = useQuery({
    queryKey: ['company-stats', company.id],
    queryFn: async () =>
      (await client.get(`/api/v1/admin/companies/${company.id}/stats`)).data,
  });

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-medium text-gray-900">
            {/* Falls back to the building icon where no logo is set, which is
                most companies for a while — an empty frame reads as a
                failure rather than as an absence. */}
            <CompanyLogo companyId={company.id} className="h-5 w-5" />
            {company.name}
          </h3>
          <p className="mt-0.5 text-sm text-gray-500">
            {[company.city, company.state].filter(Boolean).join(', ') || '—'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!company.isActive && (
            <span className="badge bg-gray-200 text-gray-600">inactive</span>
          )}
          {/* There was no way to correct a company at all before this — a
              typo in the GSTIN or the bank account was permanent. */}
          {onEdit && (
            <button
              type="button"
              className="btn-ghost px-2 py-1 text-xs"
              onClick={onEdit}
              title="Correct this company's details"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          )}
        </div>
      </div>

      <SetupChecklist company={company} offices={offices} policies={policies} />

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-gray-500">GSTIN</dt>
        <dd className="font-mono text-xs text-gray-700">{company.gstin ?? '—'}</dd>
        <dt className="text-gray-500">PAN</dt>
        <dd className="font-mono text-xs text-gray-700">{company.pan ?? '—'}</dd>
        <dt className="text-gray-500">Bank</dt>
        <dd className="font-mono text-xs text-gray-700">
          {company.bankAccountNumberMasked ?? '—'}
        </dd>
      </dl>

      {stats && (
        <div className="mt-3 flex flex-wrap gap-3 border-t border-gray-100 pt-3 text-xs text-gray-600">
          <span>{stats.userCount} users</span>
          <span>{stats.activeEmployeeCount} active staff</span>
          <span>{stats.clientCount} clients</span>
          <span>{stats.placementCount} placements</span>
        </div>
      )}

      {!company.state && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No state set. Professional Tax slabs are selected by state, so payroll
          cannot run for this company until it is filled in.
        </p>
      )}
    </div>
  );
}

function CompanyForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [values, setValues] = useState<CompanyFieldValues>(EMPTY_COMPANY);

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(companyPayload(values));
      }}
    >
      {/* The same controls the edit dialog uses. Two screens over one endpoint
          pair, and every field duplicated between them is a field that
          eventually disagrees. */}
      <CompanyFields
        values={values}
        onChange={setValues}
        accountHint="Stored server-side and never returned in full."
      />
      <div className="mt-3 flex justify-end">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Create company
        </button>
      </div>
    </form>
  );
}

function SetupChecklist({
  company,
  offices,
  policies,
}: {
  company: Company;
  offices: number | undefined;
  policies: number | undefined;
}) {
  const actingCompanyId = useAuth((s) => s.actingCompanyId);

  // Still loading. Saying "no workplace" before the answer arrives would
  // accuse a configured company of being broken for as long as the request
  // takes.
  if (offices === undefined || policies === undefined) return null;
  if (offices > 0 && policies > 0) {
    return (
      <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
        <Check className="h-3.5 w-3.5" />
        {offices} workplace{offices === 1 ? '' : 's'} · {policies} monitoring
        {' '}polic{policies === 1 ? 'y' : 'ies'}
      </p>
    );
  }

  const missing = [
    offices === 0 ? 'a workplace' : null,
    policies === 0 ? 'an attendance monitoring policy' : null,
  ].filter(Boolean);

  return (
    <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2">
      <p className="flex items-start gap-1.5 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Not set up yet — this company has no {missing.join(' and no ')}.
          Anyone hired into it now runs against hardcoded 08:30–17:30 times
          with nothing recorded about which, and sits outside the attendance
          report.
        </span>
      </p>
      {/* Workplaces reads the acting company, so an owner has to be acting as
          this one to configure it. Saying which rather than sending them to a
          screen showing somebody else's rows. */}
      <p className="mt-1.5 pl-5 text-xs text-amber-800">
        {actingCompanyId === company.id ? (
          <Link to="/admin/workplaces" className="underline hover:text-amber-900">
            Set it up under Workplaces
          </Link>
        ) : (
          <>Switch to {company.name} in the company picker, then set it up
          under Workplaces.</>
        )}
      </p>
    </div>
  );
}
